<template>
  <div class="flex flex-col w-full max-w-3xl mx-auto p-6 fadeIn">
    <h2 class="text-2xl">Add New Referrer</h2>

    <Form v-model="form" @submit="handleSubmit" class="space-y-6 relative py-4">
      <!-- Name -->
      <FormField name="name">
        <FormItem>
          <FormLabel>Name <span class="text-red-500">*</span></FormLabel>
          <FormControl>
            <Input v-model="form.name" placeholder="Enter referrer's name" required />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <!-- Email -->
      <FormField name="email">
        <FormItem>
          <FormLabel>Email <span class="text-red-500">*</span></FormLabel>
          <FormControl>
            <Input
              v-model="form.email"
              type="email"
              placeholder="Enter referrer's email"
              required
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <!-- LinkedIn URL -->
      <FormField name="linkedin_url">
        <FormItem>
          <FormLabel>LinkedIn URL</FormLabel>
          <FormControl>
            <Input
              v-model="form.linkedin_url"
              type="url"
              placeholder="https://www.linkedin.com/in/username"
              @blur="validateLinkedInUrl(form.linkedin_url)"
              @input="validateLinkedInUrl(form.linkedin_url)"
              :class="{ 'border-red-500': linkedinUrlError }"
            />
          </FormControl>
          <p v-if="linkedinUrlError" class="text-sm text-red-500 mt-1">
            {{ linkedinUrlError }}
          </p>
        </FormItem>
      </FormField>

      <!-- Employers -->
      <FormField name="employers">
        <FormItem>
          <FormLabel>Companies</FormLabel>
          <FormControl>
            <EmployerMultiSelect v-model="form.employers" placeholder="Select companies..." />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <!-- Relationship Type -->
      <FormField name="relationship_type">
        <FormItem>
          <FormLabel>Relationship Type</FormLabel>
          <FormControl>
            <Select v-model="form.relationship_type">
              <SelectTrigger>
                <SelectValue placeholder="Select relationship type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NON_GRAD_CHAMPION">Non Grad Champion</SelectItem>
                <SelectItem value="GRAD">Grad</SelectItem>
                <SelectItem value="RECRUITER_MANAGER">Recruiter/Manager</SelectItem>
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <!-- Follow Up Method -->
      <FormField name="follow_up_method">
        <FormItem>
          <FormLabel>Follow-up Method</FormLabel>
          <FormControl>
            <Input
              v-model="form.follow_up_method"
              placeholder="How to follow up with this referrer"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <!-- Passion Area -->
      <FormField name="passion_area">
        <FormItem>
          <FormLabel>Passion Area</FormLabel>
          <FormControl>
            <Input
              v-model="form.passion_area"
              placeholder="Areas the referrer is passionate about"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <!-- Reachability -->
      <FormField name="reachability">
        <FormItem>
          <FormLabel>Reachability</FormLabel>
          <FormControl>
            <Select v-model="form.reachability">
              <SelectTrigger>
                <SelectValue placeholder="Select reachability" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EASY">Easy</SelectItem>
                <SelectItem value="HARD">Hard</SelectItem>
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <!-- Notes -->
      <FormField name="notes">
        <FormItem>
          <FormLabel>Notes</FormLabel>
          <FormControl>
            <Textarea
              v-model="form.notes"
              placeholder="Additional notes about this referrer"
              rows="3"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <!-- Active Status -->
      <FormField name="is_active">
        <FormItem>
          <FormLabel>Status</FormLabel>
          <FormControl>
            <Select v-model="form.is_active">
              <SelectTrigger>
                <SelectValue>
                  {{ form.is_active === 'active' ? 'Active' : 'Inactive' }}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <!-- Submit Button -->
      <div class="flex justify-end pt-4">
        <Button type="submit" :disabled="isSubmitting">
          <Loader2 v-if="isSubmitting" class="mr-2 h-4 w-4 animate-spin" />
          Add Referrer
        </Button>
      </div>
    </Form>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { Loader2 } from 'lucide-vue-next'
import { useReferrersStore } from '@/stores/referrers'
import EmployerMultiSelect from '@/components/ui/EmployerMultiSelect.vue'
import {
  Form,
  FormField,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import type { RelationshipType, ReachDifficulty } from '@/types/referrer'
import { useToast } from '@/components/ui/toast'

const router = useRouter()
const referrersStore = useReferrersStore()
const isSubmitting = ref(false)
const { toast } = useToast()

const form = ref({
  name: '',
  email: '',
  is_active: 'active' as 'active' | 'inactive',
  employers: [] as number[],
  relationship_type: '' as RelationshipType | '',
  follow_up_method: '',
  passion_area: '',
  reachability: '' as ReachDifficulty | '',
  notes: '',
  linkedin_url: ''
})

const linkedinUrlError = ref('')

const validateLinkedInUrl = (url: string) => {
  if (!url) {
    linkedinUrlError.value = ''
    return true
  }
  
  try {
    const urlObj = new URL(url)
    // Check if protocol is http or https
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      linkedinUrlError.value = 'URL must use http or https protocol'
      return false
    }
    linkedinUrlError.value = ''
    return true
  } catch {
    linkedinUrlError.value = 'Please enter a valid URL'
    return false
  }
}

const handleSubmit = async () => {
  try {
    isSubmitting.value = true

    // Validate LinkedIn URL before submitting
    if (!validateLinkedInUrl(form.value.linkedin_url)) {
      isSubmitting.value = false
      return
    }

    // Create a clean copy of the form data, removing empty strings
    const formData = {
      ...form.value,
      is_active: form.value.is_active === 'active',
      relationship_type: form.value.relationship_type || undefined,
      reachability: form.value.reachability || undefined,
      follow_up_method: form.value.follow_up_method || undefined,
      passion_area: form.value.passion_area || undefined,
      notes: form.value.notes || undefined,
      linkedin_url: form.value.linkedin_url || undefined
    }

    await referrersStore.createReferrer(formData)
    toast({
      title: 'Success',
      description: 'Referrer created successfully',
      variant: 'default'
    })
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push('/referrers')
  } catch (error) {
    console.error('Failed to create referrer:', error)
    toast({
      title: 'Error',
      description: 'Failed to create referrer',
      variant: 'destructive'
    })
  } finally {
    isSubmitting.value = false
  }
}
</script>
